import { useState } from 'react';
import { useApiQuery } from '../use-api.js';

interface CostPr {
  org: string;
  repo: string;
  pr: number;
  total_usd: number;
  verdict: string;
}

interface CostResponse {
  total_spend_usd: number;
  total_artifacts: number;
  auto_approved_usd: number;
  human_required_usd: number;
  top_prs: CostPr[];
}

function todayStr(): string {
  return new Date().toISOString().slice(0, 10);
}

function daysAgoStr(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

export function CostView() {
  const [from, setFrom] = useState(daysAgoStr(30));
  const [to, setTo] = useState(todayStr());

  const path = `/api/cost?org=rewindio&from=${from}&to=${to}`;
  const { data, isLoading, error } = useApiQuery<CostResponse>(['cost', from, to], path);

  function fmt(n: number | undefined): string {
    if (n === undefined || n === null) return '—';
    return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }

  return (
    <div>
      <h2 className="text-lg font-semibold mb-4">Cost View</h2>

      <div className="flex gap-3 mb-6 flex-wrap items-end">
        <div>
          <label className="block text-xs text-gray-500 mb-1">From</label>
          <input
            type="date"
            value={from}
            onChange={e => setFrom(e.target.value)}
            className="border rounded px-3 py-1.5 text-sm"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">To</label>
          <input
            type="date"
            value={to}
            onChange={e => setTo(e.target.value)}
            className="border rounded px-3 py-1.5 text-sm"
          />
        </div>
      </div>

      {isLoading && <p className="text-sm text-gray-500">Loading…</p>}
      {error && <p className="text-sm text-red-600">Error: {String(error)}</p>}

      {data && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
            <div className="bg-white border rounded-lg p-4">
              <div className="text-xs text-gray-400 mb-1">Total spend</div>
              <div className="text-xl font-semibold">{fmt(data.total_spend_usd)}</div>
            </div>
            <div className="bg-white border rounded-lg p-4">
              <div className="text-xs text-gray-400 mb-1">Artifacts with cost</div>
              <div className="text-xl font-semibold">{data.total_artifacts ?? '—'}</div>
            </div>
            <div className="bg-white border rounded-lg p-4">
              <div className="text-xs text-gray-400 mb-1">Auto-approved</div>
              <div className="text-xl font-semibold">{fmt(data.auto_approved_usd)}</div>
            </div>
            <div className="bg-white border rounded-lg p-4">
              <div className="text-xs text-gray-400 mb-1">Human-required</div>
              <div className="text-xl font-semibold">{fmt(data.human_required_usd)}</div>
            </div>
          </div>

          <h3 className="text-sm font-semibold text-gray-700 mb-2">Top 5 most expensive PRs</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white border rounded-lg text-sm">
              <thead className="bg-gray-50 text-left">
                <tr>
                  <th className="px-4 py-2 font-medium text-gray-600">Org</th>
                  <th className="px-4 py-2 font-medium text-gray-600">Repo</th>
                  <th className="px-4 py-2 font-medium text-gray-600">PR</th>
                  <th className="px-4 py-2 font-medium text-gray-600">Verdict</th>
                  <th className="px-4 py-2 font-medium text-gray-600 text-right">Cost</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {(data.top_prs ?? []).length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-4 py-6 text-center text-gray-400">No data.</td>
                  </tr>
                )}
                {(data.top_prs ?? []).map(p => (
                  <tr key={`${p.org}/${p.repo}/${p.pr}`} className="hover:bg-gray-50">
                    <td className="px-4 py-2">{p.org}</td>
                    <td className="px-4 py-2 font-mono text-xs">{p.repo}</td>
                    <td className="px-4 py-2">#{p.pr}</td>
                    <td className="px-4 py-2">{p.verdict}</td>
                    <td className="px-4 py-2 text-right">{fmt(p.total_usd)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="text-xs text-gray-400 mt-6">
            Note: Recharts bar chart deferred to v2. Numbers rendered as tables for MVP.
          </p>
        </>
      )}
    </div>
  );
}
